import { ChangeDetectionStrategy, Component, effect, inject, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { tryLoadAccountUsers } from '@capital-access/account/users';
import { getFeatureToggle } from '@capital-access/common/feature-toggle';
import { LocalizationService } from '@capital-access/common/localization';
import {
  DEFAULT_TEMPLATE_KEY,
  ITINERARY_MAX_ACTIVITY_SELECTION_LIMIT,
  ITINERARY_MAX_EXTERNAL_PARTICIPANT_LIMIT,
  LoadStatus,
  SECTIONS_LAYOUT_KEY
} from '@capital-access/common/utils';
import { Alert, Breakpoint, ChipSize, ChipType, FireflyDrawerService } from '@capital-access/firefly/components';
import {
  createConfigurableItineraryReport,
  EXPORT_REPORT_ITINERARY_ACTIVITIES,
  EXPORT_REPORT_ITINERARY_EVENT,
  ReportTemplate,
  ReportType
} from '@capital-access/reporting/common';
import { BaseReportDrawer } from '../../../components/report-drawer/base-report-drawer';
import { saveReportDefaultTemplate, saveReportLayoutSettings } from '../../../core/+state/report.actions';
import { ReportSection, SectionSelectorListItem } from '../../../core/models/report-sections';
import { getSelectedSectionsFromGroups } from '../../../core/utils/section-settings.utils';
import { createItineraryCustomReport } from '../../+state/itinerary-report.actions';
import { SETTINGS_KEY } from '../../constants';
import { GroupedSectionSelectorListItem } from '../../models/itinerary-groupped-section-item';
import { CreateItineraryReportDto } from '../../models/itinerary-report.models';
import {
  DEFAULT_DISABLED_TOOLTIP,
  getSectionCustomDisabledTooltip,
  ItineraryReportLayout,
  SECTIONS_WEIGHT_LIMIT
} from '../../models/itinerary-report-sections';
import { ItineraryTemplatesStore } from '../../services/templates.store';

@Component({
  templateUrl: './itinerary-report-drawer.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ItineraryTemplatesStore],
  standalone: false
})
export class ItineraryReportDrawerComponent
  extends BaseReportDrawer<CreateItineraryReportDto, GroupedSectionSelectorListItem[]>
  implements OnInit
{
  totalLimitReached: boolean = false;
  alertType = Alert.Info;
  layout: ItineraryReportLayout = ItineraryReportLayout.Standard;
  chipSize = ChipSize.Sm;
  ChipType = ChipType;
  ItineraryReportLayout = ItineraryReportLayout;
  isConfigurableDisabled: boolean = false;
  configurableDisableReason: 'activityLimit' | 'externalParticipantLimit' | undefined;
  isStandardDisabled: boolean = false;
  readonly templatesStore = inject(ItineraryTemplatesStore);
  readonly activityLimit = ITINERARY_MAX_ACTIVITY_SELECTION_LIMIT;
  readonly externalParticipantLimit = ITINERARY_MAX_EXTERNAL_PARTICIPANT_LIMIT;

  isConfigurableToggle = toSignal(this.store.select(getFeatureToggle('configurable-itinerary-export-workflows')));

  onTabSwitch($event: unknown) {
    this.switchOffSetAsDefault();
    this.setLayout($event);
  }

  setLayout($event: unknown) {
    this.layout = ($event as { id: ItineraryReportLayout }).id;
    if (this.layout === ItineraryReportLayout.Configurable) {
      this.loadConfigurableTemplates();
      // Hide footer when switching to Configurable layout if there are no templates
      const isLoading = this.templatesStore.loadStatus() === LoadStatus.Loading;
      const noData = !this.templatesStore.templates().length;
      const shouldHide = isLoading || noData;
      this.drawerService.changeDrawerFooterVisibility(shouldHide);
    } else {
      this.settingsChange(this.form.controls.sections.value);
      this.drawerService.changeDrawerFooterVisibility(false);
    }
  }

  loadConfigurableTemplates() {
    this.templatesStore.loadTemplates(ReportType.Itinerary);
  }

  saveLayoutSetting() {
    this.store.dispatch(
      saveReportLayoutSettings({
        key: `${ReportType.Itinerary}_${SECTIONS_LAYOUT_KEY}`.toUpperCase(),
        layout: this.layout.toString()
      })
    );
  }

  saveDefaultTemplateSetting() {
    if (this.form.controls.setAsDefault.touched && this.form.controls.setAsDefault.value) {
      this.saveLayoutSetting();
      // For Standard layout there is no template; store '0' as a sentinel so we can detect
      // on reopen that Standard was explicitly set as the default.
      const defaultTemplateId =
        this.layout === ItineraryReportLayout.Standard ? '0' : this.selectedTemplateId?.toString() ?? null;
      this.store.dispatch(
        saveReportDefaultTemplate({
          key: `${ReportType.Itinerary}_${DEFAULT_TEMPLATE_KEY}`.toUpperCase(),
          defaultTemplateId
        })
      );
    }
  }

  ngOnInit() {
    const sections = this.form.controls.sections.value;
    const weightSum = this.calculateWeightSum(sections);
    this.setTotalLimitReached(weightSum);
  }

  override getSections(filteredSections: GroupedSectionSelectorListItem[]): SectionSelectorListItem[] {
    return filteredSections;
  }

  get settingsKey(): string {
    return SETTINGS_KEY;
  }

  get createReportAction() {
    const sections = getSelectedSectionsFromGroups(this.form.controls.sections.value);
    return createItineraryCustomReport({
      eventId: this.request!.eventId,
      eventName: this.request!.eventName,
      sections
    });
  }

  override createReport() {
    if (this.layout === ItineraryReportLayout.Configurable && this.isConfigurableToggle()) {
      this.saveDefaultTemplateSetting();
      const activities = this.request?.activities ?? [];
      const eventName = (activities.length === 1 ? activities[0].subject : null) ?? this.request!.eventName ?? '';

      this.store.dispatch(
        createConfigurableItineraryReport({
          eventId: activities.length === 0 ? this.request!.eventId : '',
          templateId: this.selectedTemplateId!,
          eventName,
          activityIds: activities.map(a => a.activityId) ?? []
        })
      );
      this.drawerInstanceRef.setConfirmationModalTrigger(false);
      this.close({ reportInProgress: true });
    } else {
      if (this.isConfigurableToggle()) {
        this.saveDefaultTemplateSetting();
      }
      super.createReport();
    }
  }

  override onInjectInputs(inputs: {
    request?: CreateItineraryReportDto;
    filteredSections: GroupedSectionSelectorListItem[];
    isConfigurableDisabled?: boolean;
    configurableDisableReason?: 'activityLimit' | 'externalParticipantLimit';
    layout?: ItineraryReportLayout;
    wasConfigurableDefaultOverridden?: boolean;
    preloadedTemplates?: ReportTemplate[];
  }) {
    this.store.dispatch(tryLoadAccountUsers());
    super.onInjectInputs(inputs);
    this.isConfigurableDisabled = !!inputs.isConfigurableDisabled;
    this.configurableDisableReason = inputs.configurableDisableReason;
    this.isStandardDisabled = !inputs.request?.eventId;
    if (inputs.preloadedTemplates?.length) {
      this.templatesStore.seedTemplates(inputs.preloadedTemplates);
    }
    if (inputs.layout && this.isConfigurableToggle() && !this.isMobile) {
      if (this.isConfigurableToggle() && inputs.wasConfigurableDefaultOverridden) {
        this.switchOffSetAsDefault();
      }
      if (inputs.layout !== this.layout) {
        this.setLayout({ id: inputs.layout });
      }
    } else if (
      this.isStandardDisabled &&
      this.isConfigurableToggle() &&
      !this.isConfigurableDisabled &&
      !this.isMobile
    ) {
      this.setLayout({ id: ItineraryReportLayout.Configurable });
    }
  }

  selectionChange(sections: GroupedSectionSelectorListItem[]) {
    super.settingsChange(sections);
    const weightSum = this.calculateWeightSum(sections);
    this.setTotalLimitReached(weightSum);
    this.form.controls.sections.setValue(this.setToggleDisabled(sections, weightSum), { emitEvent: true });
  }

  private calculateWeightSum(sections: GroupedSectionSelectorListItem[]): number {
    const allSections = sections.flatMap((s: GroupedSectionSelectorListItem) => s.nestedItems || [s]);
    return allSections
      .filter((s: GroupedSectionSelectorListItem) => s.isSelected)
      .reduce((sum: number, s: GroupedSectionSelectorListItem) => sum + (s.weight || 0), 0);
  }

  private isTotalLimitReached(sumWeight: number): boolean {
    return sumWeight === SECTIONS_WEIGHT_LIMIT;
  }

  private setTotalLimitReached(weightSum: number) {
    this.totalLimitReached = this.isTotalLimitReached(weightSum);
  }

  private setToggleDisabled(
    sections: GroupedSectionSelectorListItem[],
    sumWeight: number
  ): GroupedSectionSelectorListItem[] {
    return sections.map((s: GroupedSectionSelectorListItem) => {
      const isToggleDisabled =
        !s.weight || s.isSelected ? s.isToggleDisabled : sumWeight + s.weight > SECTIONS_WEIGHT_LIMIT;

      return {
        ...s,
        isToggleDisabled,
        nestedItems: s.nestedItems ? this.setToggleDisabled(s.nestedItems, sumWeight) : undefined,
        disabledTooltip: this.setDisabledTooltip(s.id, this.totalLimitReached, !!isToggleDisabled)
      };
    });
  }

  private setDisabledTooltip(id: ReportSection, totalLimitReached: boolean, disabled: boolean) {
    if (!disabled) {
      return;
    }
    if (totalLimitReached) {
      return DEFAULT_DISABLED_TOOLTIP;
    }

    return getSectionCustomDisabledTooltip(id);
  }

  get isMobile() {
    return window.innerWidth < Breakpoint.Sm;
  }

  get selectedTemplateId(): number | undefined {
    return this.templatesStore.selectedTemplate()?.templateId;
  }

  previewAndCustomize() {
    this.saveDefaultTemplateSetting();
    if (this.request?.activities?.length) {
      localStorage.removeItem(EXPORT_REPORT_ITINERARY_EVENT);
      localStorage.setItem(EXPORT_REPORT_ITINERARY_ACTIVITIES, JSON.stringify(this.request.activities));
    } else {
      localStorage.removeItem(EXPORT_REPORT_ITINERARY_ACTIVITIES);
      const event = {
        eventId: this.request?.eventId,
        name: this.request?.eventName,
        startDate: this.request?.startDate,
        endDate: this.request?.endDate,
        type: this.request?.eventType
      };
      localStorage.setItem(EXPORT_REPORT_ITINERARY_EVENT, JSON.stringify(event));
    }
    if (this.form.controls.setAsDefault.touched && this.form.controls.setAsDefault.value) {
      this.templatesStore.setDefaultTemplate(this.templatesStore.templates(), this.selectedTemplateId ?? 0);
      this.form.markAsPristine();
    }
  }

  constructor(store: Store, drawerService: FireflyDrawerService, private localizationService: LocalizationService) {
    super(store, drawerService);
    effect(() => {
      const isLoading = this.templatesStore.loadStatus() === LoadStatus.Loading;
      const noData = !this.templatesStore.templates().length;
      const shouldHide = (isLoading || noData) && this.layout === ItineraryReportLayout.Configurable;
      drawerService.changeDrawerFooterVisibility(shouldHide);
    });
  }
}
